import {
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Radio,
  addToast,
} from "@heroui/react";
import { LimeAlert } from "@/Components/NextBase/LimeAlert";
import { LimeModal } from "@/Components/NextBase/LimeModal";
import { LimeRadioGroup } from "@/Components/NextBase/LimeRadioGroup";
import { Trans } from "@lingui/react/macro";
import { t } from "@lingui/core/macro";
import * as Sentry from "@sentry/react";
import { api } from "@/lib/api-client";
import { getApiErrorMessage } from "@/utils/api-error";
import { useEffect, useState } from "react";
import {
  formatMemberCount,
  isDocumentSharedWithOtherMembers,
  type PeriodDocument,
} from "./subscriptionInvoiceUtils";

type DocumentAction = "cancel" | "keep";

type Props = {
  invoice: {
    id: number;
    documents: PeriodDocument[];
  } | null;
  onClose: () => void;
};

function documentLabel(type: PeriodDocument["type"]): string {
  return type === "invoice" ? t`račun` : t`predračun`;
}

function defaultActions(
  documents: PeriodDocument[],
): Record<string, DocumentAction> {
  return Object.fromEntries(
    documents.map((document) => [
      document.documentId,
      document.canceled ? "keep" : "cancel",
    ]),
  );
}

function SharedDocumentWarning({ memberCount }: { memberCount: number }) {
  const members = formatMemberCount(memberCount);
  return (
    <LimeAlert
      color="warning"
      data-identifier="delete-period-shared-document-warning"
      title={t`Dokument zajema ${members} in bo preklican pri vseh.`}
      description={t`Obdobja ostalih članov bo mogoče znova zaračunati.`}
    />
  );
}

export function SubscriptionInvoiceDeletePeriodModal({
  invoice,
  onClose,
}: Props) {
  const [actions, setActions] = useState<Record<string, DocumentAction>>({});

  useEffect(() => {
    if (invoice != null) {
      setActions(defaultActions(invoice.documents));
    }
  }, [invoice]);

  const { mutateAsync: deleteInvoice, isPending } =
    api.customer.useDeleteSubscriptionInvoice();

  const handleClose = () => {
    onClose();
  };

  const handleConfirm = async () => {
    if (invoice == null) {
      Sentry.captureMessage(
        "SubscriptionInvoiceDeletePeriodModal: handleConfirm aborted — missing invoice",
        "warning",
      );
      return;
    }
    const cancelDocumentIds = invoice.documents
      .filter(
        (document) =>
          !document.canceled && actions[document.documentId] === "cancel",
      )
      .map((document) => document.documentId);
    try {
      await deleteInvoice({
        invoiceId: invoice.id,
        body: { cancelDocumentIds },
      });
      addToast({
        title: t`Obdobje je bilo uspešno izbrisano`,
        color: "success",
      });
      handleClose();
    } catch (error) {
      Sentry.captureException(error);
      addToast({
        title: t`Napaka pri brisanju obdobja`,
        description:
          getApiErrorMessage(error) ??
          t`Prišlo je do napake. Poskusite ponovno.`,
        color: "danger",
      });
    }
  };

  const documents = invoice?.documents ?? [];

  return (
    <LimeModal
      isOpen={invoice != null}
      onClose={handleClose}
      data-identifier="subscription-invoice-delete-period-modal"
    >
      <ModalContent>
        {() => (
          <>
            <ModalHeader>
              <Trans>Izbriši obdobje</Trans>
            </ModalHeader>
            <ModalBody>
              {documents.length === 0 ? (
                <p className="text-default-700 text-sm">
                  <Trans>
                    Ali ste prepričani, da želite izbrisati obdobje?
                  </Trans>
                </p>
              ) : (
                <>
                  <p className="text-sm">
                    {documents.length === 1 ? (
                      <Trans>
                        Obdobje bo izbrisano. Kaj naj se zgodi z dokumentom?
                      </Trans>
                    ) : (
                      <Trans>
                        Obdobje bo izbrisano. Kaj naj se zgodi z vsakim
                        dokumentom?
                      </Trans>
                    )}
                  </p>
                  {documents.map((document) => (
                    <div
                      key={document.documentId}
                      className="flex flex-col gap-1"
                      data-identifier="delete-period-document"
                    >
                      <p className="text-default-700 text-sm font-medium">
                        {document.payerName} — {documentLabel(document.type)}{" "}
                        {document.number ?? ""}
                      </p>
                      <LimeRadioGroup
                        size="sm"
                        orientation="horizontal"
                        value={actions[document.documentId] ?? "keep"}
                        onValueChange={(value) =>
                          setActions((previous) => ({
                            ...previous,
                            [document.documentId]: value as DocumentAction,
                          }))
                        }
                        isDisabled={document.canceled}
                        description={
                          document.canceled ? (
                            <Trans>
                              Dokument je že preklican v davčni blagajni.
                            </Trans>
                          ) : undefined
                        }
                        data-identifier="delete-period-document-action-radio"
                      >
                        <Radio
                          value="cancel"
                          data-identifier="delete-period-document-cancel"
                        >
                          {document.type === "invoice" ? (
                            <Trans>Storniraj v davčni blagajni</Trans>
                          ) : (
                            <Trans>Prekliči v davčni blagajni</Trans>
                          )}
                        </Radio>
                        <Radio
                          value="keep"
                          data-identifier="delete-period-document-keep"
                        >
                          <Trans>Pusti v davčni blagajni</Trans>
                        </Radio>
                      </LimeRadioGroup>
                      {isDocumentSharedWithOtherMembers(document) &&
                        actions[document.documentId] === "cancel" && (
                          <SharedDocumentWarning
                            memberCount={document.memberCount}
                          />
                        )}
                    </div>
                  ))}
                </>
              )}
            </ModalBody>
            <ModalFooter>
              <Button variant="light" onPress={handleClose}>
                <Trans>Prekliči</Trans>
              </Button>
              <Button
                color="danger"
                onPress={handleConfirm}
                isLoading={isPending}
                data-identifier="confirm-delete-period-button"
              >
                <Trans>Izbriši</Trans>
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </LimeModal>
  );
}
